import React, { useEffect, useState } from 'react';
import { addDoc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { locationDoc, locationsColl } from '../db';
import { Screen, UserProfile } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

interface Place {
  id: string;
  name: string;
  note: string;
  lat?: number;
  lng?: number;
  addedBy: string;
  addedByName: string;
}

export default function LocationMap({ user, navigate }: Props) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(locationsColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => setPlaces(snap.docs.map(d => ({ id: d.id, ...d.data() } as Place))));
  }, [user.coupleId]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    });
  };

  const add = async () => {
    if (!name.trim()) return;
    await addDoc(locationsColl(user.coupleId), {
      name: name.trim(),
      note: note.trim(),
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      addedBy: user.uid,
      addedByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setName('');
    setNote('');
    setCoords(null);
    setOpen(false);
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('memories')}>←</button>
        <h1 style={styles.title}>Yer Haritası</h1>
      </div>

      <div className="feature-page">
        <section className="feature-hero-card map-hero">
          <p className="eyebrow">Gittiğimiz yerler</p>
          <h2>İlişkimizin küçük haritası.</h2>
          <button className="feature-primary" onClick={() => setOpen(true)}>Yer ekle</button>
        </section>

        <section className="place-list">
          {places.map(place => (
            <article key={place.id} className="place-card">
              <div className="place-pin">⌖</div>
              <div>
                <h3>{place.name}</h3>
                {place.note && <p>{place.note}</p>}
                <small>{place.addedByName}</small>
              </div>
              <div className="place-actions">
                {typeof place.lat === 'number' && typeof place.lng === 'number' && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`} target="_blank" rel="noreferrer">Aç</a>
                )}
                {place.addedBy === user.uid && <button onClick={() => deleteDoc(locationDoc(user.coupleId, place.id))}>Sil</button>}
              </div>
            </article>
          ))}
          {places.length === 0 && <div className="empty-state">Henüz yer yok. İlk gittiğiniz özel yeri ekleyelim.</div>}
        </section>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Yer ekle</h2>
            <p className="modal-sub">İstersen mevcut konumu da iliştir.</p>
            <input placeholder="Yer adı" value={name} onChange={e => setName(e.target.value)} style={{ marginBottom: 10 }} />
            <textarea placeholder="Bu yerin hikayesi" value={note} onChange={e => setNote(e.target.value)} style={{ marginBottom: 10 }} />
            <button className="btn btn-ghost" onClick={getCurrentLocation} style={{ marginBottom: 10 }}>
              {coords ? 'Konum alındı' : 'Şu anki konumu kullan'}
            </button>
            <button className="btn btn-primary" onClick={add}>Kaydet</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
};
